<template>
  <div class="container">
    <BasicTitle :title="$route.name" />
    <BasicSubtitle subtitle="Los campos marcados con (*) son obligatorios." />
    <div class="container-form">
      <BasicLabel label="Apartamento *" />
      <BorderSelect
        v-model="form.apartmentId"
        label="Apartamento *"
        :options="apartmentOptions"
        placeholder="Seleccione"
      />
      <FormError :show="Boolean(errors.apartmentId)" :message="errors.apartmentId" />

      <BasicLabel label="Tipo de usuario *" />
      <BorderSelect
        v-model="form.payerType"
        label="Tipo de usuario *"
        :options="payerTypeOptions"
        placeholder="Seleccione"
      />
      <FormError :show="Boolean(errors.payerType)" :message="errors.payerType" />

      <BasicLabel label="Cuenta contable *" />
      <BorderSelect
        v-model="form.accountId"
        label="Cuenta contable *"
        :options="accountOptions"
        placeholder="Seleccione"
      />
      <FormError :show="Boolean(errors.accountId)" :message="errors.accountId" />

      <BasicLabel label="Moneda *" />
      <BorderSelect
        v-model="form.currencyId"
        label="Moneda *"
        :options="currencyOptions"
        placeholder="Seleccione"
      />
      <FormError :show="Boolean(errors.currencyId)" :message="errors.currencyId" />

      <BasicLabel label="Monto *" />
      <BorderInput
        v-model="form.amount"
        label="Monto *"
        type="number"
        step="0.01"
        min="0"
        @keyup.enter="onSave"
      />
      <FormError :show="Boolean(errors.amount)" :message="errors.amount" />

      <BasicLabel label="Forma de pago *" />
      <BorderSelect
        v-model="form.paymentMethodId"
        label="Forma de pago *"
        :options="paymentMethodOptions"
        placeholder="Seleccione"
      />
      <FormError :show="Boolean(errors.paymentMethodId)" :message="errors.paymentMethodId" />

      <BasicLabel label="Cuenta bancaria" />
      <BorderSelect
        v-model="form.bankAccountId"
        label="Cuenta bancaria"
        :options="bankAccountOptions"
        placeholder="Sin cuenta"
      />

      <BasicLabel label="Fecha *" />
      <BorderInput
        v-model="form.date"
        label="Fecha *"
        type="date"
        :max="maxDate"
        @change="validateDate"
      />
      <FormError :show="Boolean(errors.date)" :message="errors.date" />

      <BasicLabel label="Comentario" />
      <BorderInput v-model="form.comment" label="Comentario" type="textarea" />

      <BasicLabel label="Adjuntar comprobantes" />
      <div class="file-upload">
        <input
          :id="incomeUploadId"
          ref="fileInput"
          class="file-upload__input"
          type="file"
          accept="image/*,.pdf"
          @change="onFileChange"
        />
        <label class="file-upload__label" :for="incomeUploadId">
          <span class="file-upload__icon pi pi-upload" aria-hidden="true"></span>
          <div class="file-upload__text">
            <span class="file-upload__title">Haz clic para seleccionar tus comprobantes</span>
            <span class="file-upload__hint"
              >Archivos PDF o imágenes.</span
            >
          </div>
        </label>
      </div>
      <p class="file-upload__helper">El archivo se guardará al confirmar el ingreso.</p>

      <div v-if="form.attachments.length" class="file-upload__section">
        <p class="file-upload__subtitle">Comprobantes guardados</p>
        <ul class="file-upload__list">
          <li
            v-for="file in form.attachments"
            :key="storedAttachmentKey(file)"
            class="file-upload__list-item"
          >
            <span class="file-upload__filename">
              {{ storedAttachmentLabel(file) }}
            </span>
            <div class="file-upload__meta">
              <button
                type="button"
                class="file-upload__action"
                @click="handleStoredAttachmentClick(file)"
              >
                Ver
              </button>
              <button
                type="button"
                class="file-upload__action file-upload__action--danger"
                @click="removeStoredAttachment(file)"
              >
                Eliminar
              </button>
            </div>
          </li>
        </ul>
      </div>

      <div v-if="pendingAttachments.length" class="file-upload__section">
        <p class="file-upload__subtitle">Archivo pendiente por subir</p>
        <ul class="file-upload__list">
          <li v-for="file in pendingAttachments" :key="file.uid" class="file-upload__list-item">
            <span class="file-upload__filename">{{ file.displayName }}</span>
            <div class="file-upload__meta">
              <span class="file-upload__tag">Pendiente</span>
              <button
                type="button"
                class="file-upload__action file-upload__action--danger"
                @click="removePendingAttachment(file.uid)"
              >
                Quitar
              </button>
            </div>
          </li>
        </ul>
      </div>
    </div>
    <PrimaryButton :label="primaryButtonLabel" :click="onSave" />
  </div>
</template>

<script>
import { computed, reactive, ref, onMounted, watch } from "vue";
import { useRouter } from "vue-router";
import store from "../../store";
import BasicTitle from "../../widgets/title/BasicTitle";
import BasicLabel from "../../widgets/label/BasicLabel";
import BasicSubtitle from "../../widgets/subtitle/BasicSubtitle";
import BorderInput from "../../widgets/input/BorderInput";
import BorderSelect from "../../widgets/select/BorderSelect";
import FormError from "../../widgets/error/FormError";
import PrimaryButton from "../../widgets/button/PrimaryButton";
import { openMode, actions, aws, validation } from "../../constants";
import { sortAccountsByCode } from "../../utils/accounting/accounts.mjs";

const createEmptyErrors = () => ({
  apartmentId: "",
  accountId: "",
  paymentMethodId: "",
  currencyId: "",
  payerType: "",
  amount: "",
  date: "",
});

const toOptionList = (items = [], { valueKey = "id", labelFn } = {}) =>
  (items || [])
    .filter(Boolean)
    .map((item) => {
      const rawValue = item[valueKey] ?? item.id;
      if (rawValue === undefined || rawValue === null) return null;
      const label =
        (labelFn && labelFn(item)) ||
        item.name ||
        item.label ||
        String(rawValue);
      const trimmedLabel = label?.toString().trim();
      if (!trimmedLabel) return null;
      return { id: String(rawValue), name: trimmedLabel };
    })
    .filter(Boolean);

export default {
  components: {
    BasicTitle,
    BasicLabel,
    BasicSubtitle,
    BorderInput,
    BorderSelect,
    FormError,
    PrimaryButton,
  },
  setup() {
    const router = useRouter();

    const selectedBuilding = computed(() => store.state.general.selectedBuilding);
    const buildingId = computed(() => {
      const value = selectedBuilding.value;
      if (!value) return null;
      return typeof value === "object" ? value.id : value;
    });

    const isEditMode = computed(() => store.state.openMode === openMode.MODIFY);
    const primaryButtonLabel = computed(() => (isEditMode.value ? "Actualizar" : "Guardar"));
    const payerTypeOptions = [
      { id: "owner", name: "Propietario" },
      { id: "tenant", name: "Inquilino" },
    ];

    const form = reactive({
      number: "",
      buildingId: null,
      apartmentId: "",
      payerType: "",
      accountId: "",
      bankAccountId: "",
      paymentMethodId: "",
      currencyId: "",
      date: "",
      amount: "",
      comment: "",
      attachments: [],
    });

    const errors = reactive(createEmptyErrors());
    const pendingAttachments = ref([]);
    const fileInput = ref(null);
    const incomeUploadId = "income-upload-input";
    const maxDate = computed(() => new Date().toISOString().slice(0, 10));

    const apartmentOptions = computed(() =>
      toOptionList(store.state.general.apartments, {
        labelFn: (item) => item.name || item.number || item.code || String(item.id),
      })
    );

    const incomeAccounts = computed(() =>
      sortAccountsByCode(
        (store.state.account.accounts || []).filter(
          (item) => item?.type === "income"
        )
      )
    );

    const accountOptions = computed(() =>
      toOptionList(incomeAccounts.value, {
        labelFn: (item) =>
          `${item.code ? `${item.code} - ` : ""}${item.name || item.description || item.code}`,
      })
    );

    const bankAccountOptions = computed(() => {
      const options = toOptionList(store.state.bankAccount.bankAccounts, {
        labelFn: (item) => {
          const bank = item.bank?.name ? ` - ${item.bank.name}` : "";
          return `${item.accountNumber || item.name || item.id}${bank}`.trim();
        },
      });
      return [{ id: "", name: "Sin cuenta" }, ...options];
    });

    const currencyOptions = computed(() =>
      toOptionList(store.state.general.currencies, {
        labelFn: (item) => [item.code, item.name].filter(Boolean).join(" - "),
      })
    );

    const paymentMethodOptions = computed(() =>
      toOptionList(store.state.general.paymentMethods, {
        labelFn: (item) => item.name || item.description || String(item.id),
      })
    );

    const selectedIncomeId = computed(
      () => store.state.income.selectedIncomes?.[0]?.id || store.state.income.current?.id || null
    );

    const clearErrors = () => {
      Object.keys(errors).forEach((key) => {
        errors[key] = "";
      });
    };

    const normalizeStoredAttachment = (attachment) => {
      if (!attachment) return null;
      const path = typeof attachment.path === "string" ? attachment.path : "";
      const name = typeof attachment.name === "string" ? attachment.name : "";
      const ext = typeof attachment.ext === "string" ? attachment.ext : "";
      if (!path || !name || !ext) return null;
      const link =
        typeof attachment.link === "string" && attachment.link
          ? attachment.link
          : `${path}${name}${ext}`;
      const displayName =
        attachment.displayName ||
        attachment.originalName ||
        attachment.fileName ||
        `${name}${ext}`;
      return {
        ...attachment,
        path,
        name,
        ext,
        link,
        displayName,
      };
    };

    const resetForm = () => {
      form.number = "";
      form.buildingId = buildingId.value || null;
      form.apartmentId = "";
      form.payerType = "";
      form.accountId = "";
      form.bankAccountId = "";
      form.paymentMethodId = "";
      form.currencyId = "";
      form.date = maxDate.value;
      form.amount = "";
      form.comment = "";
      form.attachments = [];
      pendingAttachments.value = [];
      if (fileInput.value) {
        fileInput.value.value = "";
      }
      clearErrors();
    };

    const populateForm = (income) => {
      if (!income) return;
      form.number = income.number || "";
      form.buildingId = income.buildingId ?? buildingId.value ?? null;
      form.apartmentId = income.apartmentId ? String(income.apartmentId) : "";
      form.payerType = income.payerType ? String(income.payerType) : "";
      form.accountId = income.accountId ? String(income.accountId) : "";
      form.bankAccountId = income.bankAccountId ? String(income.bankAccountId) : "";
      form.paymentMethodId = income.paymentMethodId ? String(income.paymentMethodId) : "";
      form.currencyId = income.currencyId ? String(income.currencyId) : "";
      form.date = income.date || maxDate.value;
      form.amount = income.amount !== undefined && income.amount !== null ? String(income.amount) : "";
      form.comment = income.comment || "";
      form.attachments = Array.isArray(income.attachments)
        ? income.attachments.map(normalizeStoredAttachment).filter(Boolean).slice(0, 1)
        : [];
      pendingAttachments.value = [];
      if (fileInput.value) {
        fileInput.value.value = "";
      }
      clearErrors();
    };

    const ensureGeneralData = async () => {
      if (!store.state.general.currencies?.length || !store.state.general.paymentMethods?.length) {
        await store.dispatch("GENERAL_DATA");
      }
    };

    const loadCatalogs = async (id) => {
      if (!id) return;
      const requests = [
        store.dispatch(actions.generalActions.apartments, { buildingId: id }),
        store.dispatch(actions.accountActions.findAll, { buildingId: id }),
        store.dispatch(actions.bankaccountActions.findAll, { buildingId: id }),
        store.dispatch(actions.commonExpenseActions.findAllClosure, { buildingId: id }),
      ];
      const results = await Promise.allSettled(requests);
      const rejected = results.find((result) => result.status === "rejected");
      if (rejected) {
        console.error(rejected.reason);
        alert("No fue posible cargar los catalogos requeridos.");
      }
    };

    const loadIncome = async () => {
      if (!isEditMode.value || !selectedIncomeId.value) {
        resetForm();
        return;
      }
      const response = await store.dispatch(actions.incomeActions.findOne, selectedIncomeId.value);
      if (response?.ok) {
        populateForm(response.data);
      } else {
        alert(response?.message || "No fue posible cargar el ingreso seleccionado.");
        router.push("/contabilidad/ingreso/listado");
      }
    };

    const isDateAllowed = (dateStr) => {
      if (!dateStr) return false;
      const selected = new Date(dateStr);
      if (Number.isNaN(selected.getTime())) return false;
      const today = new Date(maxDate.value);
      if (selected > today) return false;
      const closures = store.state.commonExpense?.closures || [];
      const selectedMonth = selected.getUTCMonth() + 1;
      const selectedYear = selected.getUTCFullYear();
      const closedPeriod = closures.some(
        (closure) =>
          Number(closure.buildingId) === Number(buildingId.value) &&
          Number(closure.month) === selectedMonth &&
          Number(closure.year) === selectedYear &&
          closure.status === "closed"
      );
      return !closedPeriod;
    };

    const validate = () => {
      clearErrors();
      if (!form.apartmentId) {
        errors.apartmentId = "Seleccione un apartamento";
      }
      if (!form.payerType) {
        errors.payerType = "Seleccione el tipo de usuario";
      }
      if (!form.accountId) {
        errors.accountId = "Seleccione una cuenta contable";
      }
      if (!form.paymentMethodId) {
        errors.paymentMethodId = "Seleccione la forma de pago";
      }
      if (!form.currencyId) {
        errors.currencyId = "Seleccione una moneda";
      }
      const amountValue = Number(form.amount);
      if (!Number.isFinite(amountValue) || amountValue <= 0) {
        errors.amount = "Ingrese un monto mayor a cero";
      }
      if (!form.date) {
        errors.date = "Seleccione una fecha";
      } else if (!isDateAllowed(form.date)) {
        errors.date = "La fecha no es valida para el edificio";
      }
      return !Object.values(errors).some((value) => value);
    };

    const validateDate = () => {
      if (!form.date) {
        errors.date = "Seleccione una fecha";
      } else if (!isDateAllowed(form.date)) {
        errors.date = "La fecha no es valida para el edificio";
      } else {
        errors.date = "";
      }
    };

    const onFileChange = (event) => {
      const file = event?.target?.files?.[0];
      if (!file) return;
      pendingAttachments.value = [
        {
          uid: `${file.name}-${file.size}-${file.lastModified}-${Math.random()
            .toString(36)
            .slice(2)}`,
          name: file.name,
          displayName: file.name,
          size: file.size,
          type: file.type,
          file,
        },
      ];
      if (event?.target) {
        event.target.value = "";
      }
    };

    const removePendingAttachment = (uid) => {
      pendingAttachments.value = pendingAttachments.value.filter((item) => item.uid !== uid);
    };

    const storedAttachmentKey = (attachment) => {
      if (!attachment) return "";
      const path = attachment.path || "";
      const name = attachment.name || "";
      const ext = attachment.ext || "";
      return `${path}|${name}|${ext}`;
    };

    const storedAttachmentLink = (attachment) => {
      if (!attachment) return "#";
      if (attachment.link) return attachment.link;
      const path = attachment.path || "";
      const name = attachment.name || "";
      const ext = attachment.ext || "";
      return `${path}${name}${ext}`;
    };

    const storedAttachmentLabel = (attachment) => {
      if (!attachment) return "Archivo";
      return (
        attachment.displayName ||
        attachment.originalName ||
        attachment.fileName ||
        `${attachment.name || "archivo"}${attachment.ext || ""}`
      );
    };

    const handleStoredAttachmentClick = (attachment) => {
      const link = storedAttachmentLink(attachment);
      if (!link || link === "#") return;
      if (/^https?:\/\//i.test(link)) {
        if (typeof window !== "undefined") {
          window.open(link, "_blank", "noopener,noreferrer");
        }
        return;
      }
      const downloadName =
        storedAttachmentLabel(attachment) || `${attachment.name || "archivo"}${attachment.ext || ""}`;
      try {
        aws.downloadFile(downloadName, link, (_, error) => {
          if (error) {
            notifyUploadIssue("No fue posible descargar el comprobante.");
          }
        });
      } catch (error) {
        console.error(error);
        alert("No fue posible descargar el comprobante.");
      }
    };

    const removeStoredAttachment = () => {
      form.attachments = [];
    };

    const buildAttachmentFolderName = () => {
      if (form.number) {
        return `Ingreso-${String(form.number)}`;
      }
      if (selectedIncomeId.value) {
        return `Ingreso-${selectedIncomeId.value}`;
      }
      const baseDate = form.date || new Date().toISOString().slice(0, 10);
      const suffix = Math.random().toString(36).slice(2, 8);
      return `Ingreso-${baseDate}-${suffix}`;
    };

    const prepareStorageInfo = (basePath, attachment) => {
      const extensionMatch = validation.extensionRegex.exec(attachment.name);
      const extRaw = extensionMatch && extensionMatch[0] ? extensionMatch[0] : "";
      const ext = extRaw.startsWith(".") ? extRaw : `.${extRaw.replace(/^\.+/, "")}`;
      const nameWithoutExt = extRaw
        ? attachment.name.slice(0, -extRaw.length)
        : attachment.name;
      const normalized = (nameWithoutExt || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-zA-Z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "")
        .toLowerCase();
      const timestamp = Date.now();
      const randomSuffix = Math.random().toString(36).slice(2, 8);
      const storedName = `${normalized || "archivo"}-${timestamp}-${randomSuffix}`;
      const finalName = `${storedName}${ext}`;
      const path = basePath.endsWith("/") ? basePath : `${basePath}/`;
      return {
        key: `${path}${finalName}`,
        storedName,
        ext,
        link: `${path}${finalName}`,
        path,
        displayName: attachment.displayName || attachment.name,
      };
    };

    const notifyUploadIssue = (message) => {
      if (store.state.toast?.add) {
        store.state.toast.add({
          severity: "warn",
          summary: "",
          detail: message,
          life: 5000,
        });
      } else {
        alert(message);
      }
    };

    const uploadPendingAttachments = async () => {
      if (!pendingAttachments.value.length) return [];
      if (!buildingId.value) {
        return [];
      }
      const folderName = buildAttachmentFolderName();
      let responsePath = null;
      try {
        responsePath = await store.dispatch(actions.documentActions.getPath, {
          buildingId: buildingId.value,
          folderName,
          documentTypeName: "Ingresos",
        });
      } catch (error) {
        console.error(error);
      }
      if (!responsePath?.ok) {
        const detail =
          responsePath?.message || "No fue posible obtener la ruta destino para los comprobantes.";
        notifyUploadIssue(detail);
        pendingAttachments.value = [];
        if (fileInput.value) {
          fileInput.value.value = "";
        }
        return [];
      }

      const uploadResults = await Promise.all(
        pendingAttachments.value.map(
          (attachment) =>
            new Promise((resolve) => {
              try {
                const storageInfo = prepareStorageInfo(responsePath.path, attachment);
                aws.uploadFile(storageInfo.key, attachment.file, (data, error) => {
                  if (error || !data) {
                    resolve({
                      ok: false,
                      attachment,
                      error: error || new Error("No fue posible subir el archivo."),
                    });
                    return;
                  }
                  const link = data?.Location || storageInfo.link;
                  resolve({
                    ok: true,
                    value: {
                      path: storageInfo.path,
                      name: storageInfo.storedName,
                      ext: storageInfo.ext,
                      link,
                      displayName:
                        storageInfo.displayName ||
                        `${storageInfo.storedName}${storageInfo.ext}`,
                    },
                  });
                });
              } catch (error) {
                resolve({
                  ok: false,
                  attachment,
                  error,
                });
              }
            })
        )
      );

      const successful = [];
      const failed = [];
      uploadResults.forEach((result) => {
        if (result?.ok && result.value) {
          successful.push(result.value);
        } else if (result?.attachment) {
          const name =
            result.attachment.displayName ||
            result.attachment.name ||
            result.attachment.file?.name ||
            "archivo";
          failed.push(name);
        }
      });

      pendingAttachments.value = [];
      if (fileInput.value) {
        fileInput.value.value = "";
      }
      if (failed.length) {
        notifyUploadIssue(
          `No se pudieron subir los siguientes archivos: ${failed.join(", ")}.`
        );
      }
      return successful;
    };

    const buildPayload = () => {
      const amountValue = Number(form.amount);
      return {
        buildingId: buildingId.value,
        apartmentId: Number(form.apartmentId),
        accountId: Number(form.accountId),
        bankAccountId: form.bankAccountId ? Number(form.bankAccountId) : null,
        paymentMethodId: Number(form.paymentMethodId),
        currencyId: Number(form.currencyId),
        date: form.date,
        payerType: form.payerType,
        amount: Number.isFinite(amountValue) ? amountValue : 0,
        comment: form.comment || "",
        attachments: Array.isArray(form.attachments)
          ? form.attachments
              .map((item) => ({
                path: item.path,
                name: item.name,
                ext: item.ext,
              }))
              .filter((item) => item.path && item.name && item.ext)
              .slice(0, 1)
          : [],
      };
    };

    const onSave = async () => {
      if (!validate()) return;
      if (!buildingId.value) {
        alert("Debe seleccionar un edificio");
        return;
      }
      store.commit("setLoading", true);
      try {
        const baseAttachments = Array.isArray(form.attachments)
          ? form.attachments.map(normalizeStoredAttachment).filter(Boolean).slice(0, 1)
          : [];
        const uploaded = await uploadPendingAttachments();
        const uploadedAttachments = (uploaded || [])
          .map(normalizeStoredAttachment)
          .filter(Boolean);
        const preferredAttachments =
          uploadedAttachments.length > 0 ? uploadedAttachments : baseAttachments;
        form.attachments = preferredAttachments.slice(0, 1);
        const payload = buildPayload();
        payload.buildingId = buildingId.value;
        let response;
        if (isEditMode.value && selectedIncomeId.value) {
          response = await store.dispatch(actions.incomeActions.update, {
            id: selectedIncomeId.value,
            ...payload,
          });
        } else {
          response = await store.dispatch(actions.incomeActions.create, payload);
        }
        if (response?.ok) {
          store.commit("setToast", {
            type: "success",
            message: isEditMode.value
              ? "Ingreso actualizado correctamente."
              : "Ingreso creado correctamente.",
          });
          router.push("/contabilidad/ingreso/listado");
        } else {
          alert(response?.message || "No fue posible guardar el ingreso");
        }
      } catch (error) {
        console.error(error);
        alert("Ocurrio un error al guardar el ingreso.");
      } finally {
        store.commit("setLoading", false);
      }
    };

    onMounted(async () => {
      store.commit("setLoading", true);
      try {
        await ensureGeneralData();
        if (buildingId.value) {
          await loadCatalogs(buildingId.value);
        }
        if (isEditMode.value) {
          await loadIncome();
        } else {
          resetForm();
        }
      } finally {
        store.commit("setLoading", false);
      }
    });

    watch(buildingId, async (value, oldValue) => {
      if (value && value !== oldValue) {
        store.commit("setLoading", true);
        try {
          await loadCatalogs(value);
          if (!isEditMode.value) {
            resetForm();
            form.buildingId = value;
          }
        } finally {
          store.commit("setLoading", false);
        }
      }
    });

    return {
      store,
      form,
      errors,
      maxDate,
      apartmentOptions,
      payerTypeOptions,
      accountOptions,
      bankAccountOptions,
      currencyOptions,
      paymentMethodOptions,
      pendingAttachments,
      primaryButtonLabel,
      onSave,
      onFileChange,
      removePendingAttachment,
      removeStoredAttachment,
      storedAttachmentKey,
      storedAttachmentLink,
      storedAttachmentLabel,
      handleStoredAttachmentClick,
      validateDate,
      fileInput,
      incomeUploadId,
    };
  },
};
</script>

<style scoped>
.container {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.container-form {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 2rem 0;
  width: 100%;
  max-width: 600px;
}

.container-form > * {
  width: 100%;
  margin-bottom: 1.5rem;
}

.container-form > *:last-child {
  margin-bottom: 0;
}

</style>


